/**
 * Enumerações da Business Management API usadas na Fase 3 (papéis de BM, tarefas por ativo,
 * fusos e verticais), conferidas na referência da Graph API v26.0 em 18/10/2026.
 * Ficam aqui (e não no meta-sdk) porque a interface e os schemas Zod também precisam delas.
 */

/** Papéis de uma pessoa num Business Manager (`POST /{business_id}/business_users`, campo `role`). */
export const META_BUSINESS_ROLES = [
  "ADMIN",
  "EMPLOYEE",
  "FINANCE_EDITOR",
  "FINANCE_ANALYST",
  "ADS_RIGHTS_REVIEWER",
  "DEVELOPER",
  "PARTNER_CENTER_ADMIN",
  "PARTNER_CENTER_ANALYST",
  "PARTNER_CENTER_OPERATIONS",
  "PARTNER_CENTER_MARKETING",
  "PARTNER_CENTER_EDUCATION",
] as const;
export type MetaBusinessRole = (typeof META_BUSINESS_ROLES)[number];

export const META_BUSINESS_ROLE_LABELS: Record<MetaBusinessRole, string> = {
  ADMIN: "Administrador",
  EMPLOYEE: "Funcionário",
  FINANCE_EDITOR: "Editor financeiro",
  FINANCE_ANALYST: "Analista financeiro",
  ADS_RIGHTS_REVIEWER: "Revisor de direitos de anúncios",
  DEVELOPER: "Desenvolvedor",
  PARTNER_CENTER_ADMIN: "Admin da Central de parceiros",
  PARTNER_CENTER_ANALYST: "Analista da Central de parceiros",
  PARTNER_CENTER_OPERATIONS: "Operações da Central de parceiros",
  PARTNER_CENTER_MARKETING: "Marketing da Central de parceiros",
  PARTNER_CENTER_EDUCATION: "Educação da Central de parceiros",
};

/** Papéis que podem ser escolhidos ao convidar (os demais são atribuídos pela própria Meta). */
export const META_INVITABLE_BUSINESS_ROLES = [
  "ADMIN",
  "EMPLOYEE",
  "FINANCE_EDITOR",
  "FINANCE_ANALYST",
  "ADS_RIGHTS_REVIEWER",
  "DEVELOPER",
] as const satisfies readonly MetaBusinessRole[];

/** Papéis de system user (`POST /{business_id}/system_users`, campo `role`). */
export const META_SYSTEM_USER_ROLES = ["ADMIN", "EMPLOYEE"] as const;
export type MetaSystemUserRole = (typeof META_SYSTEM_USER_ROLES)[number];

/** Tipos de ativo que podem ser atribuídos a pessoas/system users e vinculados a clientes. */
export const META_ASSET_TYPES = ["ad_account", "page", "pixel"] as const;
export type MetaAssetType = (typeof META_ASSET_TYPES)[number];

export const META_ASSET_TYPE_LABELS: Record<MetaAssetType, string> = {
  ad_account: "Conta de anúncio",
  page: "Página",
  pixel: "Pixel",
};

/** Tarefas por tipo de ativo (`POST /{asset_id}/assigned_users`, campo `tasks`). */
export const META_ASSET_TASKS: Record<MetaAssetType, readonly string[]> = {
  ad_account: ["MANAGE", "ADVERTISE", "ANALYZE", "DRAFT", "AA_ANALYZE"],
  page: ["MANAGE", "CREATE_CONTENT", "MODERATE", "MESSAGING", "ADVERTISE", "ANALYZE"],
  pixel: ["EDIT", "ANALYZE"],
};

export const META_TASK_LABELS: Record<string, string> = {
  MANAGE: "Gerenciar",
  ADVERTISE: "Anunciar",
  ANALYZE: "Analisar",
  DRAFT: "Rascunhos",
  AA_ANALYZE: "Analisar (Advantage+)",
  CREATE_CONTENT: "Criar conteúdo",
  MODERATE: "Moderar",
  MESSAGING: "Mensagens",
  EDIT: "Editar",
};

export function metaTaskLabel(task: string): string {
  return META_TASK_LABELS[task] ?? task;
}

/** Tarefas que um parceiro pode receber numa conta de anúncio (`/agencies`, `permitted_tasks`). */
export const META_PARTNER_AD_ACCOUNT_TASKS = ["MANAGE", "ADVERTISE", "ANALYZE"] as const;

/**
 * Fusos aceitos ao criar conta (`timezone_id`), com o id numérico da Meta
 * (referência: Ad Account → Timezone IDs). Lista enxuta: Brasil, Américas e Europa.
 */
export const META_TIMEZONES = [
  { id: 25, name: "America/Sao_Paulo", label: "Brasília (America/Sao_Paulo)" },
  { id: 248, name: "America/Manaus", label: "Manaus (America/Manaus)" },
  { id: 272, name: "America/Recife", label: "Recife (America/Recife)" },
  { id: 223, name: "America/Fortaleza", label: "Fortaleza (America/Fortaleza)" },
  { id: 205, name: "America/Bahia", label: "Salvador (America/Bahia)" },
  { id: 24, name: "America/Belem", label: "Belém (America/Belem)" },
  { id: 217, name: "America/Cuiaba", label: "Cuiabá (America/Cuiaba)" },
  { id: 23, name: "America/Campo_Grande", label: "Campo Grande (America/Campo_Grande)" },
  { id: 274, name: "America/Rio_Branco", label: "Rio Branco (America/Rio_Branco)" },
  { id: 269, name: "America/Porto_Velho", label: "Porto Velho (America/Porto_Velho)" },
  { id: 209, name: "America/Boa_Vista", label: "Boa Vista (America/Boa_Vista)" },
  { id: 22, name: "America/Noronha", label: "Fernando de Noronha (America/Noronha)" },
  { id: 10, name: "America/Argentina/Buenos_Aires", label: "Buenos Aires" },
  { id: 41, name: "America/Santiago", label: "Santiago" },
  { id: 43, name: "America/Bogota", label: "Bogotá" },
  { id: 103, name: "America/Lima", label: "Lima" },
  { id: 94, name: "America/Mexico_City", label: "Cidade do México" },
  { id: 7, name: "America/New_York", label: "Nova York (America/New_York)" },
  { id: 6, name: "America/Chicago", label: "Chicago (America/Chicago)" },
  { id: 2, name: "America/Denver", label: "Denver (America/Denver)" },
  { id: 1, name: "America/Los_Angeles", label: "Los Angeles (America/Los_Angeles)" },
  { id: 110, name: "Europe/Lisbon", label: "Lisboa" },
  { id: 58, name: "Europe/London", label: "Londres" },
  { id: 55, name: "Europe/Madrid", label: "Madri" },
  { id: 57, name: "Europe/Paris", label: "Paris" },
  { id: 47, name: "Europe/Berlin", label: "Berlim" },
  { id: 473, name: "UTC", label: "UTC" },
] as const;

export function metaTimezoneName(id: number): string | null {
  return META_TIMEZONES.find((t) => t.id === id)?.name ?? null;
}

/** Moedas mais comuns nas contas dos clientes da agência. */
export const META_CURRENCIES = [
  "BRL",
  "USD",
  "EUR",
  "GBP",
  "ARS",
  "CLP",
  "COP",
  "MXN",
  "PEN",
] as const;

/** Verticais aceitas ao criar um Business (`POST /{user_id}/businesses`, campo `vertical`). */
export const META_BUSINESS_VERTICALS = [
  "ADVERTISING",
  "AUTOMOTIVE",
  "CONSUMER_PACKAGED_GOODS",
  "ECOMMERCE",
  "EDUCATION",
  "ENERGY_AND_UTILITIES",
  "ENTERTAINMENT_AND_MEDIA",
  "FINANCIAL_SERVICES",
  "GAMING",
  "GOVERNMENT_AND_POLITICS",
  "MARKETING",
  "ORGANIZATIONS_AND_ASSOCIATIONS",
  "PROFESSIONAL_SERVICES",
  "RETAIL",
  "TECHNOLOGY",
  "TELECOM",
  "TRAVEL",
  "NON_PROFIT",
  "RESTAURANT",
  "HEALTH",
  "LUXURY",
  "OTHER",
] as const;
export type MetaBusinessVertical = (typeof META_BUSINESS_VERTICALS)[number];

export const META_BUSINESS_VERTICAL_LABELS: Record<MetaBusinessVertical, string> = {
  ADVERTISING: "Publicidade",
  AUTOMOTIVE: "Automotivo",
  CONSUMER_PACKAGED_GOODS: "Bens de consumo",
  ECOMMERCE: "E-commerce",
  EDUCATION: "Educação",
  ENERGY_AND_UTILITIES: "Energia e serviços públicos",
  ENTERTAINMENT_AND_MEDIA: "Entretenimento e mídia",
  FINANCIAL_SERVICES: "Serviços financeiros",
  GAMING: "Jogos",
  GOVERNMENT_AND_POLITICS: "Governo e política",
  MARKETING: "Marketing",
  ORGANIZATIONS_AND_ASSOCIATIONS: "Organizações e associações",
  PROFESSIONAL_SERVICES: "Serviços profissionais",
  RETAIL: "Varejo",
  TECHNOLOGY: "Tecnologia",
  TELECOM: "Telecomunicações",
  TRAVEL: "Viagens",
  NON_PROFIT: "Sem fins lucrativos",
  RESTAURANT: "Restaurante",
  HEALTH: "Saúde",
  LUXURY: "Luxo",
  OTHER: "Outro",
};
